import { useMutation } from "@tanstack/react-query";
import { useApi } from "@/data/api";
import { trpc } from "@/trpc";
import type { Inputs } from "@satyrsmc/shared/client";

/** Data: BudgetSummary[] */
export function useBudgetsSuspense() {
  return trpc.admin.budgets.list.useSuspenseQuery();
}

/** Data: ScenarioSummary[] */
export function useScenariosSuspense() {
  return trpc.admin.scenarios.list.useSuspenseQuery();
}

/** Data: BudgetSummary[] */
export function useBudgetsOptional() {
  return trpc.admin.budgets.list.useQuery();
}

/** Data: ScenarioSummary[] */
export function useScenariosOptional() {
  return trpc.admin.scenarios.list.useQuery();
}

/** Data: Budget */
export function useBudgetSuspense(id: string) {
  return trpc.admin.budgets.get.useSuspenseQuery({ id });
}

/** Data: Budget */
export function useBudgetOptional(id: string, options?: { enabled?: boolean }) {
  return trpc.admin.budgets.get.useQuery(
    { id },
    { enabled: options?.enabled !== false && !!id, ...options },
  );
}

/** Data: Scenario */
export function useScenarioSuspense(id: string) {
  return trpc.admin.scenarios.get.useSuspenseQuery({ id });
}

// Mutations
export function useCreateScenario() {
  const api = useApi();
  const utils = trpc.useUtils();
  return useMutation({
    mutationFn: (body: { name: string; description?: string; inputs?: Inputs }) =>
      api.scenarios.create(body),
    onSuccess: () => utils.admin.scenarios.list.invalidate(),
  });
}

export function useUpdateScenario() {
  const api = useApi();
  const utils = trpc.useUtils();
  return useMutation({
    mutationFn: ({
      id,
      body,
    }: {
      id: string;
      body: { name?: string; description?: string; inputs?: Inputs };
    }) => api.scenarios.update(id, body),
    onSuccess: (_, { id }) => {
      void utils.admin.scenarios.list.invalidate();
      void utils.admin.scenarios.get.invalidate({ id });
    },
  });
}

export function useDeleteScenario() {
  const api = useApi();
  const utils = trpc.useUtils();
  return useMutation({
    mutationFn: (id: string) => api.scenarios.delete(id),
    onSuccess: () => utils.admin.scenarios.list.invalidate(),
  });
}

export function useCreateBudget() {
  const api = useApi();
  const utils = trpc.useUtils();
  return useMutation({
    mutationFn: (body: { name: string; year: number; description?: string }) =>
      api.budgets.create(body),
    onSuccess: () => utils.admin.budgets.list.invalidate(),
  });
}

export function useUpdateBudget() {
  const api = useApi();
  const utils = trpc.useUtils();
  return useMutation({
    mutationFn: ({
      id,
      body,
    }: {
      id: string;
      body: { name?: string; year?: number; description?: string };
    }) => api.budgets.update(id, body),
    onSuccess: (_, { id }) => {
      void utils.admin.budgets.list.invalidate();
      void utils.admin.budgets.get.invalidate({ id });
    },
  });
}

export function useDeleteBudget() {
  const api = useApi();
  const utils = trpc.useUtils();
  return useMutation({
    mutationFn: (id: string) => api.budgets.delete(id),
    onSuccess: () => utils.admin.budgets.list.invalidate(),
  });
}

export function useAddLineItem() {
  const api = useApi();
  const utils = trpc.useUtils();
  return useMutation({
    mutationFn: ({ budgetId, body }: { budgetId: string; body: Record<string, unknown> }) =>
      api.budgets.addLineItem(budgetId, body),
    onSuccess: (_, { budgetId }) => utils.admin.budgets.get.invalidate({ id: budgetId }),
  });
}

export function useUpdateLineItem() {
  const api = useApi();
  const utils = trpc.useUtils();
  return useMutation({
    mutationFn: ({
      budgetId,
      itemId,
      body,
    }: {
      budgetId: string;
      itemId: string;
      body: Record<string, unknown>;
    }) => api.budgets.updateLineItem(budgetId, itemId, body),
    onSuccess: (_, { budgetId }) => utils.admin.budgets.get.invalidate({ id: budgetId }),
  });
}

export function useDeleteLineItem() {
  const api = useApi();
  const utils = trpc.useUtils();
  return useMutation({
    mutationFn: ({ budgetId, itemId }: { budgetId: string; itemId: string }) =>
      api.budgets.deleteLineItem(budgetId, itemId),
    onSuccess: (_, { budgetId }) => utils.admin.budgets.get.invalidate({ id: budgetId }),
  });
}
